"use client";

import React from "react";
import { ArrowLeft, Calendar, Tag, User, ChevronRight } from "lucide-react";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
import { Link } from "@/components/common/Link";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

declare const wvcClient: any;

function RelatedPostsGrid() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
        {[...Array(3)].map((_, i) => (
          <div
            key={i}
            data-index={i}
            className="h-80 animate-pulse rounded-2xl bg-[#e7e1d5]"
          />
        ))}
      </div>
    );
  }

  if (error || !posts || posts.length === 0) {
    return null;
  }

  return (
    <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
      {posts.map((p, i) => (
        <Card
          key={p.id}
          data-index={i}
          className="flex h-full flex-col overflow-hidden rounded-2xl border border-[#e1ddd2] bg-white shadow-sm transition-all hover:-translate-y-1 hover:shadow-lg"
        >
          {p.featuredImage ? (
            <div className="h-48 w-full overflow-hidden">
              <img
                src={p.featuredImage}
                alt={p.title}
                className="h-full w-full object-cover transition-transform duration-300 hover:scale-105"
                loading="lazy"
              />
            </div>
          ) : (
            <div className="flex h-48 w-full items-center justify-center bg-[#ecf7e4]">
              <Tag className="h-8 w-8 text-[#2f7d32]/60" />
            </div>
          )}

          <CardHeader className="pb-3">
            <CardTitle className="font-default text-lg font-bold leading-tight text-[#082417] line-clamp-2">
              {p.title}
            </CardTitle>
          </CardHeader>

          <CardContent className="flex flex-grow flex-col justify-between gap-4">
            <p className="text-sm leading-relaxed text-[#52605a] line-clamp-3">
              {p.excerpt?.replace(/<[^>]*>?/gm, "") ||
                "Discover more about this exclusive golf destination in our latest travel guide."}
            </p>

            <Link to={p.link || "#"} className="mt-auto">
              <Button
                variant="outline"
                className="w-full rounded-full border-[#2f7d32] text-[#2f7d32] hover:bg-[#2f7d32] hover:text-white"
              >
                Explore Guide
                <ChevronRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardContent>
        </Card>
      ))}
    </div>
  );
}

function RelatedPosts({ currentPostId }: { currentPostId: string }) {
  const wp_query = new WP_Query({
    post_type: "post",
    per_page: 3,
    exclude: [currentPostId],
  });

  return (
    <div className="mt-24 border-t border-[#e1ddd2] pt-16">
      <div className="mb-8 flex items-center justify-between">
        <h2 className="font-default text-2xl font-bold text-[#082417] md:text-3xl">
          More from the Fairway
        </h2>

        <Link to="/Blog">
          <Button
            variant="ghost"
            className="hidden text-[#2f7d32] hover:bg-[#ecf7e4] hover:text-[#2f7d32] md:flex"
          >
            View All Articles
          </Button>
        </Link>
      </div>

      <PostsProvider wp_query={wp_query}>
        <RelatedPostsGrid />
      </PostsProvider>
    </div>
  );
}

function SinglePostContent() {
  const { posts, loading, error } = usePosts();

  if (loading) {
    return (
      <div className="container mx-auto max-w-4xl animate-pulse px-4 py-24">
        <div className="mb-8 h-8 w-24 rounded bg-[#e7e1d5]" />
        <div className="mb-6 h-12 w-4/5 rounded bg-[#e7e1d5] md:h-16" />
        <div className="mb-10 flex gap-4">
          <div className="h-6 w-32 rounded bg-[#e7e1d5]" />
          <div className="h-6 w-32 rounded bg-[#e7e1d5]" />
        </div>
        <div className="mb-12 aspect-[21/9] w-full rounded-2xl bg-[#e7e1d5]" />
        <div className="space-y-4">
          <div className="h-4 w-full rounded bg-[#e7e1d5]" />
          <div className="h-4 w-full rounded bg-[#e7e1d5]" />
          <div className="h-4 w-5/6 rounded bg-[#e7e1d5]" />
          <div className="mt-8 h-4 w-full rounded bg-[#e7e1d5]" />
          <div className="h-4 w-4/5 rounded bg-[#e7e1d5]" />
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-24 text-center">
        <h2 className="mb-4 font-default text-2xl font-bold text-red-700">
          Error Loading Post
        </h2>
        <p className="mb-8 text-[#52605a]">{error}</p>

        <Link to="/Home">
          <Button className="rounded-full bg-[#B8FF00] text-[#062417] hover:bg-[#B8FF00]/90">
            Return Home
          </Button>
        </Link>
      </div>
    );
  }

  if (!posts || posts.length === 0) {
    return (
      <div className="container mx-auto max-w-4xl px-4 py-24 text-center">
        <h2 className="mb-4 font-default text-2xl font-bold text-[#082417]">
          Article Not Found
        </h2>
        <p className="mb-8 text-[#52605a]">
          The golf guide you are looking for does not exist or has been moved.
        </p>

        <Link to="/Blog">
          <Button className="rounded-full bg-[#B8FF00] text-[#062417] hover:bg-[#B8FF00]/90">
            Back to Blog
          </Button>
        </Link>
      </div>
    );
  }

  const post = posts[0];

  return (
    <article className="container mx-auto max-w-4xl px-4 py-28 md:py-32">
      <div className="mb-8">
        <Link
          to="/Blog"
          className="inline-flex items-center text-sm font-medium text-[#52605a] transition-colors hover:text-[#2f7d32]"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back to Golf Blog
        </Link>
      </div>

      <header className="mb-10">
        {post.categories && post.categories.length > 0 && (
          <div className="mb-6 flex flex-wrap gap-2">
            {post.categories.map((cat, i) => (
              <span
                key={cat.id}
                data-index={i}
                className="inline-flex items-center rounded-full bg-[#ecf7e4] px-3 py-1 text-xs font-bold uppercase tracking-wide text-[#2f7d32]"
              >
                {cat.name}
              </span>
            ))}
          </div>
        )}

        <h1 className="mb-6 font-default text-3xl font-bold leading-tight text-[#082417] md:text-5xl lg:text-6xl">
          {post.title}
        </h1>

        <div className="flex flex-wrap items-center gap-6 text-sm text-[#52605a]">
          {post.author && (
            <div className="flex items-center gap-2">
              <Avatar className="h-8 w-8 border border-[#e1ddd2]">
                <AvatarImage src={post.author.avatar?.["48"]} alt={post.author.name} />
                <AvatarFallback>
                  <User className="h-4 w-4" />
                </AvatarFallback>
              </Avatar>
              <span className="font-semibold text-[#082417]">{post.author.name}</span>
            </div>
          )}

          {post.date && (
            <div className="flex items-center gap-2">
              <Calendar className="h-4 w-4 text-[#2f7d32]" />
              <time dateTime={post.date}>
                {new Date(post.date).toLocaleDateString("en-US", {
                  year: "numeric",
                  month: "long",
                  day: "numeric",
                })}
              </time>
            </div>
          )}
        </div>
      </header>

      {post.featuredImage && (
        <figure className="mb-12 md:mb-16">
          <img
            src={post.featuredImage}
            alt={post.title}
            className="aspect-[16/9] w-full rounded-3xl object-cover shadow-xl md:aspect-[21/9]"
            loading="lazy"
          />
        </figure>
      )}

      <div
        className="prose prose-lg max-w-none font-default leading-relaxed text-[#24372f] prose-headings:font-default prose-headings:font-bold prose-headings:text-[#082417] prose-a:text-[#2f7d32] prose-a:no-underline hover:prose-a:underline prose-img:rounded-xl prose-img:shadow-lg prose-blockquote:rounded-r-lg prose-blockquote:border-l-[#B8FF00] prose-blockquote:bg-white prose-blockquote:px-6 prose-blockquote:py-2 prose-blockquote:not-italic md:prose-xl"
        dangerouslySetInnerHTML={{
          __html: post.content || "<p>No content available for this golf guide.</p>",
        }}
      />

      {post.tags && post.tags.length > 0 && (
        <div className="mb-16 mt-16 flex flex-wrap items-center gap-2 border-t border-[#e1ddd2] pt-8">
          <Tag className="mr-2 h-5 w-5 text-[#2f7d32]" />

          {post.tags.map((tag, i) => (
            <Link key={tag.id} data-index={i} to={tag.link || "#"}>
              <span className="inline-flex items-center rounded-full bg-white px-3 py-1 text-sm text-[#082417] transition-colors hover:bg-[#ecf7e4] hover:text-[#2f7d32]">
                {tag.name}
              </span>
            </Link>
          ))}
        </div>
      )}

      {post.author && (
        <div className="mb-8 flex flex-col items-center gap-6 rounded-3xl border border-[#e1ddd2] bg-white p-8 shadow-sm sm:flex-row sm:items-start">
          <Avatar className="h-24 w-24 border-4 border-[#f7f3ea] shadow-md">
            <AvatarImage src={post.author.avatar?.["96"]} alt={post.author.name} />
            <AvatarFallback className="bg-[#ecf7e4] font-default text-2xl text-[#2f7d32]">
              {post.author.name.charAt(0)}
            </AvatarFallback>
          </Avatar>

          <div className="flex-1 text-center sm:text-left">
            <h3 className="mb-2 font-default text-xl font-bold text-[#082417]">
              Curated by {post.author.name}
            </h3>

            <p className="mb-4 leading-relaxed text-[#52605a]">
              A TeeListClub travel contributor focused on golf destinations, course discovery and practical playing guides across Asia.
            </p>

            {post.author.link && (
              <Link to={post.author.link}>
                <Button
                  variant="outline"
                  size="sm"
                  className="rounded-full border-[#2f7d32] text-[#2f7d32] hover:bg-[#2f7d32] hover:text-white"
                >
                  View All Guides
                </Button>
              </Link>
            )}
          </div>
        </div>
      )}

      <RelatedPosts currentPostId={post.id} />
    </article>
  );
}

export default function SinglePostTemplateSection() {
  const currentWPQueryParams =
    typeof window !== "undefined" && window.wvcClient
      ? window.wvcClient.getCurrentWPQueryParams()
      : { post_type: "post" };

  const wp_query = new WP_Query(currentWPQueryParams);

  return (
    <section
      data-section-id="240337"
      id="single-post-template-section"
      className="min-h-screen bg-[#f7f3ea] text-[#082417]"
    >
      <PostsProvider wp_query={wp_query}>
        <SinglePostContent />
      </PostsProvider>
    </section>
  );
}